import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private shore excursion and cruise transfer",
  name: "Florence & Pisa Private Shore Excursion from Livorno Port",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Livorno", "Florence", "Pisa", "Lucca", "Tuscany"],
  description:
    "Private Florence and Pisa shore excursion from Livorno cruise port in a Mercedes V-Class with an English-speaking chauffeur. Pier-side pickup, ship tracking and a guaranteed back-to-ship time.",
  url: "https://yourblacklimo.com/florence-pisa-shore-excursion-from-livorno",
};

const CONTENT = {
  en: {
    seoTitle: "Florence & Pisa Shore Excursion from Livorno Port | Private Tour — Your Black Limo",
    seoDesc:
      "Private Florence and Pisa shore excursion from Livorno cruise port. Mercedes V-Class, English-speaking driver, pier-side pickup, ship tracking and a guaranteed back-to-ship time. Both cities in one relaxed day.",
    overline: "Livorno Shore Excursion",
    h1: "Florence & Pisa in a Day — Private Shore Excursion from Livorno Port",
    intro:
      "The classic cruise day, done privately. Step off the ship at Livorno and see both Florence and Pisa in one unhurried day, with your own English-speaking chauffeur and a Mercedes V-Class waiting pier-side — and a comfortable margin to be back on board.",
    heroAlt: "Private Florence and Pisa shore excursion from Livorno in a black Mercedes V-Class",
    blocks: [
      {
        title: "Pier-side pickup at Livorno",
        body: "Your chauffeur meets you at the Livorno cruise terminal with a name board and tracks your ship's arrival in real time. Florence is about 90 minutes away, Pisa around 30 — so both fit comfortably into a single day ashore, with no coaches and no waiting in line.",
      },
      {
        title: "Guaranteed back-to-ship time",
        body: "Everything is planned around your all-aboard time. We build in a generous margin and keep the day relaxed, so you return to the pier with time to spare — never a rushed dash back to the ship.",
      },
    ],
    cards: {
      overline: "Your day, city by city",
      title: "What you'll see in Florence and Pisa",
      items: [
        { title: "Piazza dei Miracoli", body: "The Leaning Tower of Pisa, the Cathedral and the Baptistery — a short, easy stop on the way to or from Florence." },
        { title: "Florence Duomo & Old Town", body: "Brunelleschi's dome, the Baptistery and the Renaissance heart of the city, at your own pace." },
        { title: "Ponte Vecchio", body: "The medieval bridge of goldsmiths over the Arno — one of Florence's most photographed views." },
        { title: "Piazzale Michelangelo", body: "The panoramic terrace over Florence — the perfect final stop before the drive back to Livorno." },
      ],
    },
    note: {
      title: "How it works",
      body:
        "We track your ship's arrival and meet you pier-side with a name board. The excursion is fully private — no shared coaches, no fixed groups. Your English-speaking chauffeur adapts the pace to you and guarantees a comfortable margin for your all-aboard time. Fully licensed NCC, professional drivers, and a Mercedes V-Class for up to seven guests. Entrance fees, meals and guided walking tours are booked and paid directly by the client.",
    },
    cta: {
      title: "Plan your Florence & Pisa shore excursion from Livorno",
      secondaryLabel: "All shore excursions",
      secondaryHref: "/livorno-cruise-transfers",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Can I really see both Florence and Pisa in one day from Livorno?", a: "Yes. With a private driver you can comfortably visit Pisa's Leaning Tower and spend several hours in Florence in a single day ashore, because there is no waiting for a group or a shared coach." },
        { q: "How long is the drive from Livorno to Florence?", a: "About 90 minutes each way, with Pisa around 30 minutes from the port — which is why both cities fit into one relaxed day." },
        { q: "Will I get back to my ship on time?", a: "Always. We track your ship's schedule, plan the day around your all-aboard time and build in a generous margin, so you return to the pier relaxed and never rushed." },
        { q: "Is the tour private and with an English-speaking driver?", a: "Yes. Every excursion is fully private in a Mercedes V-Class with a professional, English-speaking chauffeur — never a shared group." },
      ],
    },
  },
  it: {
    seoTitle: "Escursione a Firenze e Pisa dal Porto di Livorno | Tour Privato — Your Black Limo",
    seoDesc:
      "Escursione privata a Firenze e Pisa dal porto crociere di Livorno. Mercedes Classe V, autista che parla inglese, prelievo al molo, monitoraggio nave e rientro garantito in orario. Entrambe le città in una giornata rilassata.",
    overline: "Escursione da Livorno",
    h1: "Firenze e Pisa in un Giorno — Escursione Privata dal Porto di Livorno",
    intro:
      "La classica giornata da crociera, ma in versione privata. Scendi dalla nave a Livorno e visita sia Firenze sia Pisa in una giornata senza fretta, con il tuo autista che parla inglese e una Mercedes Classe V ad attenderti al molo — con un comodo margine per rientrare a bordo.",
    heroAlt: "Escursione privata a Firenze e Pisa da Livorno in una Mercedes Classe V nera",
    blocks: [
      {
        title: "Prelievo al molo di Livorno",
        body: "Il tuo autista ti accoglie al terminal crociere di Livorno con un cartello e monitora l'arrivo della nave in tempo reale. Firenze è a circa 90 minuti, Pisa a circa 30 — così entrambe entrano comodamente in una sola giornata a terra, senza pullman e senza code.",
      },
      {
        title: "Rientro garantito in orario",
        body: "Tutto è pianificato attorno al tuo orario di imbarco. Prevediamo un ampio margine e manteniamo la giornata rilassata, così rientri al molo con anticipo — mai una corsa affannata verso la nave.",
      },
    ],
    cards: {
      overline: "La tua giornata, città per città",
      title: "Cosa vedrai a Firenze e Pisa",
      items: [
        { title: "Piazza dei Miracoli", body: "La Torre Pendente di Pisa, il Duomo e il Battistero — una sosta breve e comoda andando o tornando da Firenze." },
        { title: "Duomo e centro di Firenze", body: "La cupola del Brunelleschi, il Battistero e il cuore rinascimentale della città, al tuo ritmo." },
        { title: "Ponte Vecchio", body: "Il ponte medievale degli orafi sull'Arno — una delle vedute più fotografate di Firenze." },
        { title: "Piazzale Michelangelo", body: "La terrazza panoramica su Firenze — la sosta finale perfetta prima del rientro a Livorno." },
      ],
    },
    note: {
      title: "Come funziona",
      body:
        "Monitoriamo l'arrivo della nave e ti accogliamo al molo con un cartello. L'escursione è completamente privata — niente pullman condivisi, niente gruppi fissi. Il tuo autista che parla inglese adatta il ritmo a te e garantisce un comodo margine sull'orario di imbarco. NCC regolarmente autorizzato, autisti professionisti e una Mercedes Classe V fino a sette ospiti. Biglietti d'ingresso, pasti e visite guidate a piedi sono prenotati e pagati direttamente dal cliente.",
    },
    cta: {
      title: "Organizza la tua escursione a Firenze e Pisa da Livorno",
      secondaryLabel: "Tutte le escursioni",
      secondaryHref: "/livorno-cruise-transfers",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Posso davvero vedere sia Firenze sia Pisa in un giorno da Livorno?", a: "Sì. Con un autista privato puoi visitare comodamente la Torre Pendente di Pisa e trascorrere diverse ore a Firenze in una sola giornata a terra, perché non c'è attesa per un gruppo o un pullman condiviso." },
        { q: "Quanto dura il tragitto da Livorno a Firenze?", a: "Circa 90 minuti per tratta, con Pisa a circa 30 minuti dal porto — ecco perché entrambe le città entrano in una giornata rilassata." },
        { q: "Rientrerò alla nave in orario?", a: "Sempre. Monitoriamo gli orari della nave, pianifichiamo la giornata attorno all'orario di imbarco e prevediamo un ampio margine, così rientri al molo rilassato e senza fretta." },
        { q: "Il tour è privato e con autista che parla inglese?", a: "Sì. Ogni escursione è completamente privata in una Mercedes Classe V con autista professionista che parla inglese — mai un gruppo condiviso." },
      ],
    },
  },
};

export default function FlorencePisaLivorno() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="florence-pisa-livorno-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/florence-pisa-shore-excursion-from-livorno", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={"https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/6494cab432784c6969924c96a0d2fbeaadff597cf4ca7b3f821af3c7e1d3cc7e.jpeg"}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
